import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, makeStyles } from '@material-ui/core';
import _every from 'lodash/every';
import _includes from 'lodash/includes';
import _isEmpty from 'lodash/isEmpty';
import _some from 'lodash/some';
import CustomSearch from '../../shared/CustomSearch';
import DealershipFilter from './DealershipFilter';
import { sortDealerships, sortLabel } from '../utils';
import SelectAll from './SelectAll';
import DealershipList from './DealershipList';

const useStyles = makeStyles({
  container: {
    height: '100%',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  searchWrapper: {
    margin: '16px 22px',
  },
  detailsContainer: {
    padding: '8px 0px 0px 20px',
    background: 'rgba(242, 244, 247, 1)',
    height: '25vh',
    overflow: 'hidden',
    flexGrow: 1,
  },
});

const AddDealerships = ({
  handleDataChange,
  data,
  handleSelectAll,
  filters,
  filterObjectInitial,
  dealerships,
  totalDealershipCount,
  displaySelectedCount,
  onApplyFilters,
  onSearch,
  selectAllMode,
  excludedIds,
  hasMore,
  fetchNextPage,
}) => {
  const classes = useStyles();
  const [filteredDealershipArray, setFilteredDealershipArray] = useState([]);
  const [sortType, setSortType] = useState('Dealership ID');

  useEffect(() => {
    setFilteredDealershipArray(dealerships || []);
  }, [dealerships]);

  const handleSearch = useCallback(
    ({ target: { value } }) => {
      onSearch(value ?? '');
    },
    [onSearch],
  );

  const handleSort = useCallback(({ target: { value } }) => {
    setSortType(sortLabel(value));
    setFilteredDealershipArray((prev) => sortDealerships([...prev], value));
  }, []);

  const handleAll = useCallback(
    ({ target: { checked } }) => {
      handleSelectAll(checked);
    },
    [handleSelectAll],
  );

  const selectedIds = useMemo(() => data?.dealerships || [], [data?.dealerships]);

  const allFilteredSelected = useMemo(() => {
    if (selectAllMode) return excludedIds.size === 0;
    if (_isEmpty(filteredDealershipArray)) return false;
    if (selectedIds.length < totalDealershipCount) return false;
    return _every(filteredDealershipArray, (item) => _includes(selectedIds, item.id));
  }, [filteredDealershipArray, selectedIds, selectAllMode, excludedIds, totalDealershipCount]);

  const selectAllIndeterminate = useMemo(() => {
    if (selectAllMode) return excludedIds.size > 0;
    if (_isEmpty(filteredDealershipArray)) return false;
    return _some(filteredDealershipArray, (item) => _includes(selectedIds, item.id)) && !allFilteredSelected;
  }, [filteredDealershipArray, selectedIds, allFilteredSelected, selectAllMode, excludedIds]);

  return (
    <Grid container direction="column" className={classes.container}>
      <Grid item className={classes.searchWrapper}>
        <CustomSearch placeholder="Search Dealership" handleSearch={handleSearch} />
      </Grid>
      <Grid item>
        <DealershipFilter
          filters={filters}
          filterObjectInitial={filterObjectInitial}
          onApplyFilters={onApplyFilters}
          handleSort={handleSort}
          sortType={sortType}
        />
      </Grid>
      <Grid item>
        <SelectAll
          margin="16px 0px 16px 22px"
          dealershipCount={totalDealershipCount}
          selected={allFilteredSelected}
          data={data}
          indeterminate={selectAllIndeterminate}
          handleChange={handleAll}
          selectAllMode={selectAllMode}
          excludedIds={excludedIds}
          displaySelectedCount={displaySelectedCount}
        />
      </Grid>
      <Grid item className={classes.detailsContainer}>
        <DealershipList
          filteredDealershipArray={filteredDealershipArray}
          data={data}
          handleDataChange={handleDataChange}
          selectAllMode={selectAllMode}
          excludedIds={excludedIds}
          hasMore={hasMore}
          fetchNextPage={fetchNextPage}
        />
      </Grid>
    </Grid>
  );
};

export default AddDealerships;
